/**
 * Bible references as people write them ("John 1:14", "1 Cor 13:4-7",
 * "Psalm 23", "Ps. 46:10"), read into a book, a chapter and a run of verses —
 * so a verse's wording can be looked up in a translation we can ship rather
 * than written by a model (docs/17-AI-CREATIVE.md). The book codes are the
 * three-letter ones eBible.org's verse-per-line files use (`JOH`, `1CO`, `SOL`).
 */

/** The 66 books, in order: code, the name we print, and other ways people write it. */
export const SCRIPTURE_BOOKS = [
  ['GEN', 'Genesis', ['gen', 'gn']],
  ['EXO', 'Exodus', ['exod', 'ex', 'exo']],
  ['LEV', 'Leviticus', ['lev', 'lv']],
  ['NUM', 'Numbers', ['num', 'nm']],
  ['DEU', 'Deuteronomy', ['deut', 'dt', 'deu']],
  ['JOS', 'Joshua', ['josh', 'jos']],
  ['JDG', 'Judges', ['judg', 'jdg']],
  ['RUT', 'Ruth', ['ru', 'rut']],
  ['1SA', '1 Samuel', ['1 sam', '1 sa', 'i samuel', 'first samuel']],
  ['2SA', '2 Samuel', ['2 sam', '2 sa', 'ii samuel', 'second samuel']],
  ['1KI', '1 Kings', ['1 kgs', '1 ki', 'i kings', 'first kings']],
  ['2KI', '2 Kings', ['2 kgs', '2 ki', 'ii kings', 'second kings']],
  ['1CH', '1 Chronicles', ['1 chron', '1 chr', '1 ch', 'i chronicles']],
  ['2CH', '2 Chronicles', ['2 chron', '2 chr', '2 ch', 'ii chronicles']],
  ['EZR', 'Ezra', ['ezr']],
  ['NEH', 'Nehemiah', ['neh']],
  ['EST', 'Esther', ['esth', 'est']],
  ['JOB', 'Job', ['jb']],
  ['PSA', 'Psalm', ['psalms', 'ps', 'psa', 'pss']],
  ['PRO', 'Proverbs', ['prov', 'pr', 'prv']],
  ['ECC', 'Ecclesiastes', ['eccl', 'ecc', 'qoh']],
  ['SOL', 'Song of Songs', ['song of solomon', 'song', 'sos', 'canticles']],
  ['ISA', 'Isaiah', ['isa', 'is']],
  ['JER', 'Jeremiah', ['jer', 'jr']],
  ['LAM', 'Lamentations', ['lam']],
  ['EZE', 'Ezekiel', ['ezek', 'eze', 'ezk']],
  ['DAN', 'Daniel', ['dan', 'dn']],
  ['HOS', 'Hosea', ['hos']],
  ['JOE', 'Joel', ['jl']],
  ['AMO', 'Amos', ['am']],
  ['OBA', 'Obadiah', ['obad', 'ob']],
  ['JON', 'Jonah', ['jon']],
  ['MIC', 'Micah', ['mic']],
  ['NAH', 'Nahum', ['nah']],
  ['HAB', 'Habakkuk', ['hab']],
  ['ZEP', 'Zephaniah', ['zeph', 'zep']],
  ['HAG', 'Haggai', ['hag']],
  ['ZEC', 'Zechariah', ['zech', 'zec']],
  ['MAL', 'Malachi', ['mal']],
  ['MAT', 'Matthew', ['matt', 'mt', 'mat']],
  ['MAR', 'Mark', ['mk', 'mrk', 'mar']],
  ['LUK', 'Luke', ['lk', 'luk']],
  ['JOH', 'John', ['jn', 'jhn', 'joh']],
  ['ACT', 'Acts', ['ac', 'act']],
  ['ROM', 'Romans', ['rom', 'rm']],
  ['1CO', '1 Corinthians', ['1 cor', '1 co', 'i corinthians', 'first corinthians']],
  ['2CO', '2 Corinthians', ['2 cor', '2 co', 'ii corinthians', 'second corinthians']],
  ['GAL', 'Galatians', ['gal']],
  ['EPH', 'Ephesians', ['eph']],
  ['PHI', 'Philippians', ['phil', 'php', 'pp']],
  ['COL', 'Colossians', ['col']],
  ['1TH', '1 Thessalonians', ['1 thess', '1 th', 'i thessalonians']],
  ['2TH', '2 Thessalonians', ['2 thess', '2 th', 'ii thessalonians']],
  ['1TI', '1 Timothy', ['1 tim', '1 ti', 'i timothy']],
  ['2TI', '2 Timothy', ['2 tim', '2 ti', 'ii timothy']],
  ['TIT', 'Titus', ['tit']],
  ['PHM', 'Philemon', ['philem', 'phm']],
  ['HEB', 'Hebrews', ['heb']],
  ['JAM', 'James', ['jas', 'jm']],
  ['1PE', '1 Peter', ['1 pet', '1 pe', 'i peter', 'first peter']],
  ['2PE', '2 Peter', ['2 pet', '2 pe', 'ii peter', 'second peter']],
  ['1JO', '1 John', ['1 jn', '1 jo', 'i john', 'first john']],
  ['2JO', '2 John', ['2 jn', '2 jo', 'ii john']],
  ['3JO', '3 John', ['3 jn', '3 jo', 'iii john']],
  ['JUD', 'Jude', ['jud']],
  ['REV', 'Revelation', ['rev', 'rv', 'revelations', 'apocalypse']],
] as const satisfies readonly (readonly [string, string, readonly string[]])[]

export type BookCode = (typeof SCRIPTURE_BOOKS)[number][0]

const squash = (name: string) =>
  name
    .toLowerCase()
    .replace(/\./g, '')
    .replace(/^(1st|first)\s*/, '1 ')
    .replace(/^(2nd|second)\s*/, '2 ')
    .replace(/^(3rd|third)\s*/, '3 ')
    .replace(/^iii\s+/, '3 ')
    .replace(/^ii\s+/, '2 ')
    .replace(/^i\s+/, '1 ')
    .replace(/^([123])\s*/, '$1 ')
    .replace(/\s+/g, ' ')
    .trim()

const BY_NAME = new Map<string, BookCode>()
for (const [code, name, aliases] of SCRIPTURE_BOOKS) {
  BY_NAME.set(squash(name), code)
  BY_NAME.set(squash(code), code)
  for (const alias of aliases) BY_NAME.set(squash(alias), code)
}

/** The book a name means ("1 Cor", "Psalms", "Song of Solomon"), or null. */
export function bookCode(name: string): BookCode | null {
  return BY_NAME.get(squash(name)) ?? null
}

/** The name we print for a book ("Psalm", "1 Corinthians"). */
export function bookName(code: BookCode): string {
  return SCRIPTURE_BOOKS.find(([c]) => c === code)?.[1] ?? code
}

export interface ScriptureRef {
  book: BookCode
  chapter: number
  /** The first verse; null: the whole chapter. */
  from: number | null
  /** The last verse (the same as `from` for one). */
  to: number | null
}

/** Verses a card quotes at most: a passage longer than this is shown by its reference only. */
export const MAX_QUOTED_VERSES = 3

/**
 * One reference, as written: "John 1:14", "John 1:14-18", "John 1:14–18",
 * "Ps 23", "1 Cor. 13:4-7". A cross-chapter run ("John 1:50-2:3") or a list
 * ("John 1:1, 14") is not one reference: null.
 */
export function parseReference(text: string): ScriptureRef | null {
  const m =
    /^\s*((?:[123]|i{1,3}|first|second|third)?\s*[a-z][a-z .]*?)\.?\s*(\d{1,3})(?:\s*:\s*(\d{1,3})(?:\s*[-–—]\s*(\d{1,3}))?)?\s*$/i.exec(
      text,
    )
  if (!m) return null
  const book = bookCode(m[1] ?? '')
  if (!book) return null
  const chapter = Number(m[2])
  const from = m[3] ? Number(m[3]) : null
  const to = m[4] ? Number(m[4]) : from
  if (chapter < 1 || (from !== null && from < 1) || (from !== null && to !== null && to < from))
    return null
  return { book, chapter, from, to }
}

/** "John 1:14", "John 1:14–18", "Psalm 23". */
export function formatReference(ref: ScriptureRef): string {
  const head = `${bookName(ref.book)} ${ref.chapter}`
  if (ref.from === null) return head
  return ref.to !== null && ref.to !== ref.from
    ? `${head}:${ref.from}–${ref.to}`
    : `${head}:${ref.from}`
}

/** How many verses a reference names (a whole chapter: Infinity). */
export const verseCount = (ref: ScriptureRef) =>
  ref.from === null ? Number.POSITIVE_INFINITY : (ref.to ?? ref.from) - ref.from + 1

/**
 * A Psalm's first verse in the BSB carries its title ("A Psalm of David. The
 * LORD is my shepherd…"): the title is not what a card quotes. Leading
 * sentences that are only a heading (a psalm of, for the choirmaster, a
 * Maskil, according to…, a song…) are left out.
 */
export function withoutPsalmTitle(text: string): string {
  const sentence = String.raw`[^.:]*[.:][”’"]?\s+`
  const heading = new RegExp(
    String.raw`^(?:(?:A (?:Psalm|Song|Maskil|Miktam|prayer|Shiggaion)|Of (?:David|Solomon|Asaph|the sons of Korah|Heman|Ethan|Moses)|For the choirmaster|For the director|According to|With stringed instruments|To the tune of|To be sung|Set to|For the dedication|For the Sabbath|For remembrance|For Jeduthun|A love song|To bring remembrance|Of the sons of Korah|A Psalm)` +
      sentence +
      ')+',
    'i',
  )
  const out = text.replace(heading, '').trim()
  return out.length > 0 ? out : text
}
